import { Box, Button, Card, IconButton, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslationHook } from "../../../hooks";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { setLoading } from "../../../store/UI";
import { uploadFile } from "../../../api/file/uploadFile";
import { Procedure } from "../../../types/model/procedure/Procedure";

interface StageFileUploadProps {
  accountId: number;
  projectId: number;
  activityId: number;
  procedureId: number;
  stageId: number;
  onFilesUpdated?: (uploadedFiles: File[]) => void;
  procedure: Procedure;
}

export const StageFileUpload = (props: StageFileUploadProps) => {
  const { accountId, projectId, activityId, procedureId, stageId, onFilesUpdated, procedure } = props;
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const handleDrop = (droppedFiles: File[]) => {
    setPendingFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
  };

  const handleDeletePendingFile = (fileToDelete: File) => {
    setPendingFiles((prevFiles) => prevFiles.filter((f) => f !== fileToDelete));
  };

  const handleUpload = async () => {
      if (pendingFiles.length === 0) return;
      dispatch(setLoading(true));
      try {
        await Promise.all(
          pendingFiles.map((file) =>
            uploadFile({
              accountId,
              projectId,
              activityId,
              procedureId,
              stageId,
              file,
            })
          )
        );
        dispatch(setLoading(false));
        setPendingFiles([]);
        onFilesUpdated && onFilesUpdated(pendingFiles);
      } catch (error) {
        console.error("Upload error:", error);
        dispatch(setLoading(false));
      }
    };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
        'image/*': ['.jpeg', '.jpg', '.png'],
        'application/pdf': ['.pdf'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
        'application/zip': ['.zip'],
        'application/x-tar': ['.tar'],
        'application/gzip': ['.gz'],
        'application/x-7z-compressed': ['.7z'],
    }, 
    disabled: procedure.canceled || procedure.closed,
  });

  return (
    <>
      {/* Área de upload (drag and drop) */}
      <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
        <Box
          {...getRootProps()}
          sx={{
            border: isDragActive ? "2px solid gray" : "2px dashed gray",
            borderRadius: 1,
            p: 3,
            textAlign: "center",
            width: "100%",
            cursor: procedure.canceled || procedure.closed ? 'not-allowed' : 'pointer',
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 60, color: "gray", mb: 1 }} />
          <Typography>
            {isDragActive
              ? t("generic.drop-here")
              : t("generic.drag-or-click") ??
                "Drag and drop files here or click to select"}
          </Typography>
        </Box>
      </Box>

      {/* Arquivos pendentes para upload */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, mb: 3 }}>
        {pendingFiles.map((file) => (
          <Card
            key={file.name}
            variant="outlined"
            sx={{
              width: "calc(33.33% - 8px)",
              minWidth: 200,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InsertDriveFileIcon sx={{ color: "gray" }} />
              <Typography variant="body2">{file.name}</Typography>
            </Box>
            <IconButton
              onClick={() => handleDeletePendingFile(file)}
              color="error"
              size="small"
              disabled={procedure.canceled || procedure.closed}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Card>
        ))}
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleUpload}
        disabled={pendingFiles.length === 0 || procedure.canceled || procedure.closed}
      >
        {t("generic.upload") ?? "Upload Files"}
      </Button>
    </>
  );
};
